import { ConfigService } from 'app/config/config.service';
import { Asset } from 'app/shared/collections/asset';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import { ImsObject } from 'app/shared/collections/ims_object';
import { ImsTimeCollection } from 'app/shared/collections/time/ims_time_collection.factory';
import { EventRegistry } from 'app/shared/events/event_registry';
import { ObservableMapOption } from 'app/shared/map_options/MapOptionsService';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import { Destructor } from 'app/shared/utilities/Destructor';
import { IndicatorManager } from 'app/views/accessories/IndicatorManager';
import { Cesium } from 'app/cesium';
import { ViewFollowRelationshipConfig } from 'app/shared/collections/view';
import { collectRelatedAssetsUsingConfig } from 'app/shared/info_box/components/infobox/generic_infobox_utils';
import { fromUnix } from 'app/shared/utilities/time';
import { ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { SelectionService } from 'app/shared/selection/SelectionService';
import { entityTypeFromString } from 'app/agriculture/models/Entities';
import { getTimelineAppropriateUnix } from 'app/map/geometry/cesium/CesiumAgricultureUtils';

/**
 * A pretty crap function which will either get the stats for an Asset at a
 * time, or for each IP Device in an Asset at a time.
 * The only reason we need this is because the collection can't tell us how
 * to access the stats (we must have prior knowledge about the collection)
 * @param collection
 * @param isForIPDevices
 * @param asset
 * @param time
 */
function getCurrentStatsForAssetFromCollectionBasedOffSomeBoolean(
    collection: ImsTimeCollection,
    isForIPDevices: boolean,
    asset: Asset,
    time: number,
    imsItemTypeRegistry: ImsItemTypeRegistry,
    config?: ViewFollowRelationshipConfig,
): Array<ImsObject> {
    if (isForIPDevices) {
        const stats = [];
        const ipDevices = Object.values(asset.ipDevices);
        for (const ipDevice of ipDevices) {
            stats.push(...collection.getStatsUnix(ipDevice.id, time));
        }
        return stats;
    } else if (config && config.relationship_table && config.relationship_field) {
        const stats = [];
        const julianDate = Cesium.JulianDate.fromDate(fromUnix(time || 0));
        const thingsContainedInThisThing = collectRelatedAssetsUsingConfig(
            config,
            imsItemTypeRegistry,
            asset,
            julianDate,
        );

        thingsContainedInThisThing.forEach((thing) => {
            const results = collection.getStats(thing.id, julianDate) || [];
            stats.push(...results);
        });
        return stats;
    } else {
        return collection.getStatsUnix(asset.id, time);
    }
}

type IndicatorState<T> = { [assetId: number]: T };

export class StatBasedIndicatorManager<T> implements IndicatorManager<T> {
    private _state: IndicatorState<Array<T>>;
    private readonly _event: EventRegistry;
    private readonly _destructor: Destructor;
    private readonly _statCollection: ImsTimeCollection;
    private readonly _clockHandler: ClockHandler;
    private readonly _assetCollection: AssetCollection;
    private readonly _isStatCollectionForIpDevices: boolean;
    private readonly _showIndicatorStateObserver: ObservableMapOption<boolean>;
    private readonly _createIndicator: (stats: Array<ImsObject>) => Array<T> | null;
    private readonly _imsItemTypeRegistry: ImsItemTypeRegistry;
    private readonly _followRelationshipConfig: ViewFollowRelationshipConfig;
    private readonly _selectionService: SelectionService;

    /**
     * This indicator manager produces a single indicator for each asset based on the result of some functions
     * and the current stats for an asset / ip device.
     * @param assetCollection
     * @param statCollection The collection which will provide the list of stats to produce an indicator for
     * @param clockHandler
     * @param showIndicatorStateObserver An option observer thing which provides the state of whether to show these indicators or not
     * @param isStatCollectionForIpDevices A crappy boolean which decides whether stats from the {@param statCollection} are for assets or IP devices
     * @param createIndicator a function which returns the indicator to use given a list of stats
     */
    constructor(
        assetCollection: AssetCollection,
        statCollection: ImsTimeCollection,
        clockHandler: ClockHandler,
        showIndicatorStateObserver: ObservableMapOption<boolean>,
        isStatCollectionForIpDevices: boolean,
        createIndicator: (stats: Array<ImsObject>) => Array<T> | null,
        imsItemTypeRegistry: ImsItemTypeRegistry,
        selectionService: SelectionService,
        config?: ViewFollowRelationshipConfig,
    ) {
        this._createIndicator = createIndicator;
        this._isStatCollectionForIpDevices = isStatCollectionForIpDevices;
        this._statCollection = statCollection;
        this._clockHandler = clockHandler;
        this._assetCollection = assetCollection;
        this._showIndicatorStateObserver = showIndicatorStateObserver;
        this._imsItemTypeRegistry = imsItemTypeRegistry;
        this._selectionService = selectionService;

        this._update = this._update.bind(this);
        this._state = {};
        this._event = new EventRegistry();

        this._destructor = new Destructor(
            this._assetCollection.addOnChangedListener(this._update),
            this._selectionService.onSelectionChange(this._update),
            this._statCollection.addOnChangedListener(this._update),
            this._clockHandler.addOnCursorMovedListener(
                this._update,
                ConfigService.imsBaseTimeResolution,
            ),
            this._showIndicatorStateObserver.onChange(this._update),
        );
        // if we have a relationship config, make sure we update indicators when that collection changes too
        const relationshipCollection = this._imsItemTypeRegistry.getTypeDetails(
            this._followRelationshipConfig?.relationship_table,
        )?.collection;
        if (relationshipCollection) {
            this._destructor.add(relationshipCollection.addOnChangedListener(this._update));
        }
        this._followRelationshipConfig = config;
    }

    private async _update() {
        if (!this._showIndicatorStateObserver.getValue()) {
            if (Object.keys(this._state).length === 0) {
                // stay empty, we're okay
                return;
            }
            this._state = {};
            await this._event.notifyListeners();
        } else {
            // rebuild state
            this._state = {};
            // rebuild the indicators
            const assets = this._assetCollection.getAllAsArray();
            let i = assets.length;
            let asset;
            while (i--) {
                asset = assets[i];
                const entityType = entityTypeFromString(asset?.type?.name);
                const time = getTimelineAppropriateUnix(entityType, this._clockHandler);
                const stats = getCurrentStatsForAssetFromCollectionBasedOffSomeBoolean(
                    this._statCollection,
                    this._isStatCollectionForIpDevices,
                    asset,
                    time,
                    this._imsItemTypeRegistry,
                    this._followRelationshipConfig,
                );
                const indicators = this._createIndicator(stats);
                if (indicators) {
                    this._state[asset.id] = indicators;
                }
            }
            // ship it out
            await this._event.notifyListeners();
        }
    }

    public getCurrentIndicators(assetId: number): Array<T> {
        return this._state[assetId] || [];
    }

    public onChange(fn: () => void): () => void {
        return this._event.addEventListener(fn);
    }

    public destroy(): void {
        this._destructor.destroy();
    }
}
